import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtMoney, fmtNum, fmtPct, monthLabel } from "@/lib/format";
import { PageHeader, SectionTitle, AwaitingData } from "@/components/common/Page";
import { KpiCard } from "@/components/common/KpiCard";
import { StatusBadge, Money } from "@/components/common/StatusBadge";
import { Loader2, AlertTriangle, TrendingUp, ArrowRight, Ship, Users, Boxes, Activity } from "lucide-react";

const alertColor = { critical: "border-rose-500/30 bg-rose-500/10 text-rose-300", warning: "border-amber-500/30 bg-amber-500/10 text-amber-300" };

export default function Overview() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["overview"], queryFn: async () => (await api.get("/overview")).data });

  if (isLoading) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-cyan-400" /></div>;

  const f = data.financial, b = data.business;
  const hasData = data.data_freshness.has_financial_data;

  return (
    <div className="ns-fade">
      <PageHeader title="Executive Overview" subtitle={`NavScora business health · ${monthLabel(data.month)}`} testId="overview-header">
        <span className="text-xs text-slate-500">{data.data_freshness.connected_integrations}/{data.data_freshness.total_integrations} integrations connected</span>
      </PageHeader>

      {data.alerts.length > 0 && (
        <div className="mb-6 space-y-2">
          {data.alerts.map((a, i) => (
            <div key={i} className={`flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm ${alertColor[a.level] || alertColor.warning}`} data-testid={`alert-${i}`}>
              <AlertTriangle className="h-4 w-4 shrink-0" /> {a.message}
            </div>
          ))}
        </div>
      )}

      <SectionTitle className="mb-3">Financial</SectionTitle>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard testId="kpi-revenue" label="Revenue (this month)" value={hasData ? fmtMoney(f.revenue_this_month) : <AwaitingData />} accent="cyan" delay={0} />
        <KpiCard testId="kpi-mrr" label="MRR" value={f.mrr ? fmtMoney(f.mrr) : <AwaitingData />} sub={f.arr ? `ARR ${fmtMoney(f.arr)}` : null} accent="cyan" delay={40} />
        <KpiCard testId="kpi-gross-profit" label="Gross Profit" value={hasData ? fmtMoney(f.gross_profit) : <AwaitingData />} sub={hasData ? `Margin ${fmtPct(f.gross_margin)}` : null} accent="emerald" delay={80} />
        <KpiCard testId="kpi-operating-profit" label="Operating Profit" value={hasData ? fmtMoney(f.operating_profit) : <AwaitingData />} accent={f.operating_profit >= 0 ? "emerald" : "rose"} delay={120} />
        <KpiCard testId="kpi-operating-costs" label="Operating Costs" value={hasData ? fmtMoney(f.operating_costs) : <AwaitingData />} accent="amber" delay={160} />
        <KpiCard testId="kpi-cash-position" label="Cash Position" value={fmtMoney(f.cash_position)} accent="slate" delay={200} />
        <KpiCard testId="kpi-runway" label="Cash Runway" value={f.runway.months != null ? `${f.runway.months} mo` : "∞"} sub={f.runway.note || "months of cover"} accent={f.runway.months != null && f.runway.months < 6 ? "rose" : "emerald"} delay={240} />
        <KpiCard testId="kpi-receivables" label="Outstanding" value={fmtMoney(data.receivables.outstanding)} sub={`${data.receivables.overdue_count} overdue`} accent={data.receivables.overdue_count ? "rose" : "slate"} delay={280} />
      </div>

      <SectionTitle className="mb-3 mt-8">Business</SectionTitle>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        {[
          { l: "Total Customers", v: b.total_customers, icon: Users },
          { l: "Active", v: b.active_customers },
          { l: "Trial / Pilot", v: b.trial_customers },
          { l: "Founding", v: b.founding_customers },
          { l: "Products", v: b.products, icon: Boxes },
          { l: "Active Subs", v: b.active_subscriptions },
        ].map((x, i) => (
          <div key={i} className="ns-rise rounded-xl border border-[#1E2D4A] bg-[#17233B]/60 p-4" style={{ animationDelay: `${i * 40}ms` }} data-testid={`biz-${i}`}>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{x.l}</div>
            <div className="mt-1 font-mono-fin text-2xl font-bold text-slate-50">{fmtNum(x.v)}</div>
          </div>
        ))}
      </div>

      <div className="mb-3 mt-8 flex items-center justify-between">
        <SectionTitle>Product Performance</SectionTitle>
        <button onClick={() => navigate("/finance/product-profitability")} className="flex items-center gap-1 text-xs font-medium text-cyan-400 hover:text-cyan-300" data-testid="view-product-profitability">
          Full analysis <ArrowRight className="h-3 w-3" />
        </button>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {data.products.map((p, i) => (
          <div key={p.product_id} className="ns-rise rounded-xl border border-[#1E2D4A] bg-[#17233B]/70 p-5 transition-colors hover:border-[#2D3F62]" style={{ animationDelay: `${i * 50}ms` }} data-testid={`product-card-${p.product_id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <Ship className="h-4 w-4 text-cyan-400" />
                <span className="font-semibold text-slate-100">{p.product_name}</span>
              </div>
              <StatusBadge status={p.status} />
            </div>
            {p.active_customers === 0 && p.revenue === 0 ? (
              <div className="mt-4"><AwaitingData label="Awaiting data — no customers or revenue yet" /></div>
            ) : (
              <div className="mt-4 grid grid-cols-2 gap-y-3 sm:grid-cols-4">
                <Metric l="Customers" v={fmtNum(p.customers)} />
                <Metric l="Revenue" v={fmtMoney(p.revenue)} mono />
                <Metric l="Direct Cost" v={fmtMoney(p.direct_cost)} mono />
                <Metric l="Margin" v={fmtPct(p.margin_pct)} accent={p.margin_pct >= 0} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const Metric = ({ l, v, mono, accent }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wide text-slate-500">{l}</div>
    <div className={`text-sm font-semibold ${mono ? "font-mono-fin" : ""} ${accent === undefined ? "text-slate-200" : accent ? "text-emerald-400" : "text-rose-400"}`}>{v}</div>
  </div>
);
